import React from "react";
import ReactApexChart from "react-apexcharts";

const ChartCard = ({ title, children }) => (
  <div
    className="rounded border border-gray-200 bg-white p-4"
    data-report-chart="true"
    data-chart-title={title}
  >
    <h3 className="text-sm font-semibold text-gray-800 mb-3">{title}</h3>
    {children}
  </div>
);

const emptyHint = (
  <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">
    No chart data for selected filters
  </div>
);

const SessionChart = ({ sessionBreakdown }) => {
  const open = sessionBreakdown?.open || {};
  const close = sessionBreakdown?.close || {};
  const hasData =
    (open.total_stake || 0) + (close.total_stake || 0) > 0;

  if (!hasData) {
    return <ChartCard title="Open vs Close Session">{emptyHint}</ChartCard>;
  }

  return (
    <ChartCard title="Open vs Close Session">
      <ReactApexChart
        options={{
          chart: { type: "bar", toolbar: { show: false } },
          plotOptions: { bar: { columnWidth: "45%" } },
          dataLabels: { enabled: false },
          xaxis: { categories: ["Open", "Close"] },
          colors: ["#4f46e5", "#f59e0b"],
          legend: { position: "top" },
          tooltip: {
            y: { formatter: (v) => Number(v).toFixed(2) },
          },
        }}
        series={[
          {
            name: "Stake",
            data: [Number(open.total_stake) || 0, Number(close.total_stake) || 0],
          },
          {
            name: "Payout",
            data: [
              Number(open.total_payout) || 0,
              Number(close.total_payout) || 0,
            ],
          },
        ]}
        type="bar"
        height={300}
      />
    </ChartCard>
  );
};

const EntityCharts = ({
  byEntity = [],
  entityLabel,
  sessionBreakdown,
  topUsersByPayout = [],
}) => {
  const labels = byEntity.map((r) => r.name);
  const stakes = byEntity.map((r) => Number(r.total_stake) || 0);
  const payouts = byEntity.map((r) => Number(r.total_payout) || 0);
  const pls = byEntity.map((r) => Number(r.profit_loss) || 0);

  const money = (v) =>
    Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={`Stake vs Payout by ${entityLabel}`}>
          {byEntity.length ? (
            <ReactApexChart
              options={{
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: { bar: { columnWidth: "55%" } },
                dataLabels: { enabled: false },
                xaxis: {
                  categories: labels,
                  labels: {
                    rotate: -35,
                    trim: true,
                    style: { fontSize: "11px" },
                  },
                },
                colors: ["#4f46e5", "#f59e0b"],
                legend: { position: "top" },
                tooltip: {
                  y: { formatter: (v) => Number(v).toFixed(2) },
                },
              }}
              series={[
                { name: "Stake", data: stakes },
                { name: "Payout", data: payouts },
              ]}
              type="bar"
              height={300}
            />
          ) : (
            emptyHint
          )}
        </ChartCard>

        <ChartCard title={`Stake share by ${entityLabel}`}>
          {byEntity.length ? (
            <ReactApexChart
              options={{
                chart: { type: "donut" },
                labels,
                legend: { position: "bottom" },
                tooltip: {
                  y: { formatter: (v) => Number(v).toFixed(2) },
                },
              }}
              series={stakes}
              type="donut"
              height={300}
            />
          ) : (
            emptyHint
          )}
        </ChartCard>

        <ChartCard title={`Profit / Loss by ${entityLabel}`}>
          {byEntity.length ? (
            <ReactApexChart
              options={{
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: {
                  bar: { horizontal: true, distributed: true },
                },
                dataLabels: { enabled: false },
                xaxis: { categories: labels },
                colors: pls.map((v) => (v >= 0 ? "#16a34a" : "#dc2626")),
                legend: { show: false },
                tooltip: {
                  y: { formatter: (v) => Number(v).toFixed(2) },
                },
              }}
              series={[{ name: "P/L", data: pls }]}
              type="bar"
              height={300}
            />
          ) : (
            emptyHint
          )}
        </ChartCard>

        <SessionChart sessionBreakdown={sessionBreakdown} />
      </div>

      <div
        className="rounded border border-gray-200 bg-white p-4"
        data-report-chart="true"
        data-chart-title="Top 5 Users"
      >
        <h3 className="text-sm font-semibold text-gray-800 mb-3">
          Top 5 Users
        </h3>
        {topUsersByPayout.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="py-2 pr-4">#</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">User ID</th>
                  <th className="py-2 pr-4 text-right">Total Payout</th>
                  <th className="py-2 pr-4 text-right">Total Matches</th>
                  <th className="py-2 text-right">Win %</th>
                </tr>
              </thead>
              <tbody>
                {topUsersByPayout.map((u, idx) => (
                  <tr
                    key={u.user_id || idx}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-gray-500">{idx + 1}</td>
                    <td className="py-2.5 pr-4 font-medium text-gray-900">
                      {u.username || "—"}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600">{u.user_id}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">
                      {money(u.total_payout)}
                    </td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">
                      {u.total_matches ?? 0}
                    </td>
                    <td className="py-2.5 text-right tabular-nums font-medium text-indigo-600">
                      {Number(u.win_percentage || 0).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-gray-400">
            No user data for selected filters
          </div>
        )}
      </div>
    </div>
  );
};

const UserCharts = ({ topUsers = [], userOutcomes = {}, sessionBreakdown }) => {
  const labels = topUsers.map((u) => u.name || u.id);
  const stakes = topUsers.map((u) => Number(u.total_stake) || 0);
  const pls = topUsers.map((u) => Number(u.profit_loss) || 0);

  const outcomeSeries = [
    Number(userOutcomes.profitable_users) || 0,
    Number(userOutcomes.loss_making_users) || 0,
    Number(userOutcomes.break_even_users) || 0,
  ];
  const hasOutcomes = outcomeSeries.some((n) => n > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard title="Top Users by Stake">
        {topUsers.length ? (
          <ReactApexChart
            options={{
              chart: { type: "bar", toolbar: { show: false } },
              plotOptions: { bar: { horizontal: true } },
              dataLabels: { enabled: false },
              xaxis: { categories: labels },
              colors: ["#4f46e5"],
              tooltip: {
                y: { formatter: (v) => Number(v).toFixed(2) },
              },
            }}
            series={[{ name: "Stake", data: stakes }]}
            type="bar"
            height={300}
          />
        ) : (
          emptyHint
        )}
      </ChartCard>

      <ChartCard title="Top Users by Profit / Loss">
        {topUsers.length ? (
          <ReactApexChart
            options={{
              chart: { type: "bar", toolbar: { show: false } },
              plotOptions: { bar: { horizontal: true, distributed: true } },
              dataLabels: { enabled: false },
              xaxis: { categories: labels },
              colors: pls.map((v) => (v >= 0 ? "#16a34a" : "#dc2626")),
              legend: { show: false },
              tooltip: {
                y: { formatter: (v) => Number(v).toFixed(2) },
              },
            }}
            series={[{ name: "P/L", data: pls }]}
            type="bar"
            height={300}
          />
        ) : (
          emptyHint
        )}
      </ChartCard>

      <ChartCard title="User Outcome Mix">
        {hasOutcomes ? (
          <ReactApexChart
            options={{
              chart: { type: "donut" },
              labels: ["Profitable", "Loss-making", "Break-even"],
              colors: ["#16a34a", "#dc2626", "#9ca3af"],
              legend: { position: "bottom" },
            }}
            series={outcomeSeries}
            type="donut"
            height={300}
          />
        ) : (
          emptyHint
        )}
      </ChartCard>

      <SessionChart sessionBreakdown={sessionBreakdown} />
    </div>
  );
};

const ReportCharts = ({ tab, overview }) => {
  const charts = overview?.charts || {};
  const sessionBreakdown = overview?.sessionBreakdown;

  if (tab === "users") {
    return (
      <UserCharts
        topUsers={charts.topUsers || []}
        userOutcomes={charts.userOutcomes || {}}
        sessionBreakdown={sessionBreakdown}
      />
    );
  }

  return (
    <EntityCharts
      byEntity={charts.byEntity || []}
      entityLabel={tab === "matches" ? "Match" : "Market"}
      sessionBreakdown={sessionBreakdown}
      topUsersByPayout={charts.topUsersByPayout || []}
    />
  );
};

export default ReportCharts;
